import { Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/utils/errors";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCancelOrgDeletion,
  useInviteMember,
  useOrgInvitations,
  useOrgMembers,
  useOrgs,
  useRemoveMember,
  useRequestOrgDeletion,
  useRevokeInvitation,
  useTransferOwnership,
  useUpdateOrg,
} from "@/queries/orgs";
import { useUser } from "@/queries/user";

export function OrgSettingsPage() {
  const { orgId } = useParams({ from: "/orgs/$orgId/settings" });
  const orgs = useOrgs();
  const user = useUser();
  const updateOrg = useUpdateOrg(orgId);
  const org = orgs.data?.find((item) => item.id === orgId);
  const [name, setName] = useState("");

  useEffect(() => {
    if (org?.name) {
      setName(org.name);
    }
  }, [org?.name]);

  return (
    <div className="space-y-6">
      <Helmet><title>{org?.name ? `TodoLLM - ${org.name} Settings` : "TodoLLM - Settings"}</title></Helmet>
      <div className="space-y-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/orgs/$orgId" params={{ orgId }}>
            <ArrowLeft className="size-4" />
            Back to projects
          </Link>
        </Button>
        <h2 className="text-3xl font-semibold tracking-tight">Organization settings</h2>
        <p className="text-sm text-muted-foreground">
          Update the organization name and review the workspace identifier.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>General</CardTitle>
          <CardDescription>
            Organization administrators can rename the workspace from here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim() || org?.role === "member") return;
              updateOrg.mutate(
                { name: name.trim() },
                {
                  onSuccess: () => toast.success("Organization updated"),
                  onError: (error) => toast.error(getErrorMessage(error)),
                },
              );
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization name</Label>
              <Input
                id="org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Robotics"
                disabled={org?.role === "member"}
              />
            </div>
            {org?.role !== "member" && (
              <Button type="submit" disabled={updateOrg.isPending || !org}>
                {updateOrg.isPending ? "Saving..." : "Save changes"}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>

      {(org?.role === "owner" || org?.role === "admin") && (
        <MembersCard orgId={orgId} currentUserId={user.data?.user_id ?? ""} currentRole={org.role} />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Workspace info</CardTitle>
          <CardDescription>Useful when wiring integrations or debugging access.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Organization ID:</span>{" "}
            <code>{orgId}</code>
          </p>
          <p>
            <span className="font-medium text-foreground">Your role:</span>{" "}
            {org?.role ?? "Loading..."}
          </p>
        </CardContent>
      </Card>

      {org?.role === "owner" && (
        <Card>
          <CardHeader>
            <CardTitle>Billing</CardTitle>
            <CardDescription>
              Manage the subscription, seats, and task usage for this organization.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/orgs/$orgId/billing" params={{ orgId }}>
                Open billing dashboard
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {org?.role === "owner" && (
        <DangerZone
          orgId={orgId}
          pendingDeletionAt={org.pending_deletion_at}
        />
      )}
    </div>
  );
}

function MembersCard({
  orgId,
  currentUserId,
  currentRole,
}: {
  orgId: string;
  currentUserId: string;
  currentRole: string;
}) {
  const members = useOrgMembers(orgId);
  const invitations = useOrgInvitations(orgId);
  const removeMember = useRemoveMember(orgId);
  const inviteMember = useInviteMember(orgId);
  const revokeInvitation = useRevokeInvitation(orgId);
  const transferOwnership = useTransferOwnership(orgId);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"owner" | "admin" | "member">(
    "member",
  );
  const [transferTo, setTransferTo] = useState("");

  const nonOwnerMembers = members.data?.filter(
    (m) => m.user_id !== currentUserId && m.role !== "owner",
  ) ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Members</CardTitle>
        <CardDescription>Manage org members and pending invitations.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {members.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-2">
            {members.data?.map((m) => (
              <div key={m.user_id} className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {m.display_name ?? m.user_id}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{m.role}</Badge>
                  {m.user_id !== currentUserId && m.role !== "owner" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={removeMember.isPending}
                      onClick={() =>
                        removeMember.mutate(m.user_id, {
                          onSuccess: () => toast.success("Member removed"),
                          onError: (e) => toast.error(getErrorMessage(e)),
                        })
                      }
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <p className="text-sm font-medium">Invite member</p>
          <div className="flex gap-2">
            <Input
              placeholder="email@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1"
            />
            <Select
              value={inviteRole}
              onValueChange={(v) =>
                setInviteRole(v as "owner" | "admin" | "member")
              }
            >
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <Button
              disabled={inviteMember.isPending || !inviteEmail.trim()}
              onClick={() => {
                if (!inviteEmail.trim()) return;
                inviteMember.mutate(
                  { invitee_email: inviteEmail.trim(), role: inviteRole },
                  {
                    onSuccess: () => {
                      toast.success("Invitation sent");
                      setInviteEmail("");
                    },
                    onError: (e) => toast.error(getErrorMessage(e)),
                  },
                );
              }}
            >
              {inviteMember.isPending ? "Sending…" : "Send invite"}
            </Button>
          </div>
        </div>

        {invitations.data && invitations.data.filter((i) => i.status === "pending").length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Pending invitations</p>
            {invitations.data
              .filter((i) => i.status === "pending")
              .map((inv) => (
                <div key={inv.id} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{inv.invitee_email}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{inv.role}</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={revokeInvitation.isPending}
                      onClick={() =>
                        revokeInvitation.mutate(inv.id, {
                          onSuccess: () => toast.success("Invitation revoked"),
                          onError: (e) => toast.error(getErrorMessage(e)),
                        })
                      }
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        )}

        {currentRole === "owner" && nonOwnerMembers.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Transfer ownership</p>
            <div className="flex gap-2">
              <Select value={transferTo} onValueChange={setTransferTo}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select new owner" />
                </SelectTrigger>
                <SelectContent>
                  {nonOwnerMembers.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.display_name ?? m.user_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                disabled={transferOwnership.isPending || !transferTo}
                onClick={() => {
                  if (!transferTo) return;
                  if (!confirm("Transfer ownership? You will become an admin.")) return;
                  transferOwnership.mutate(transferTo, {
                    onSuccess: () => {
                      toast.success("Ownership transferred");
                      setTransferTo("");
                    },
                    onError: (e) => toast.error(getErrorMessage(e)),
                  });
                }}
              >
                {transferOwnership.isPending ? "Transferring…" : "Transfer"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DangerZone({
  orgId,
  pendingDeletionAt,
}: {
  orgId: string;
  pendingDeletionAt: string | null;
}) {
  const requestDeletion = useRequestOrgDeletion(orgId);
  const cancelDeletion = useCancelOrgDeletion(orgId);
  const [confirming, setConfirming] = useState(false);

  if (pendingDeletionAt) {
    const at = new Date(pendingDeletionAt);
    return (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">Deletion scheduled</CardTitle>
          <CardDescription>
            This organization will be permanently deleted on{" "}
            {at.toLocaleString()}. New projects and invitations are blocked
            until then.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            disabled={cancelDeletion.isPending}
            onClick={() =>
              cancelDeletion.mutate(undefined, {
                onSuccess: () => toast.success("Deletion canceled"),
                onError: (e) => toast.error(getErrorMessage(e)),
              })
            }
          >
            {cancelDeletion.isPending ? "Canceling..." : "Cancel deletion"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-destructive/30">
      <CardHeader>
        <CardTitle className="text-destructive">Danger zone</CardTitle>
        <CardDescription>
          Deleting an organization removes its projects, tasks, and members.
          The deletion takes effect at the end of the current billing period.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {confirming ? (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="destructive"
              disabled={requestDeletion.isPending}
              onClick={() =>
                requestDeletion.mutate(undefined, {
                  onSuccess: () => {
                    toast.success("Deletion scheduled");
                    setConfirming(false);
                  },
                  onError: (e) => toast.error(getErrorMessage(e)),
                })
              }
            >
              {requestDeletion.isPending ? "Scheduling..." : "Yes, delete this organization"}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setConfirming(true)}>
            Delete organization
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
